import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concat,
  concatMap,
  exhaustMap,
  filter,
  firstValueFrom,
  from,
  map,
  of,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { RuntimeConfigService } from '../../config/runtime-config.service';
import { ConnectivityService } from '../../offline/connectivity.service';
import { OfflineDb } from '../../offline/db';
import { SyncOperation } from '../../offline/sync-operation.model';
import { SyncQueueActions } from './sync-queue.actions';

const MAX_ATTEMPTS = 10;
const BACKOFF_MS = [0, 2_000, 8_000, 30_000, 120_000];

@Injectable()
export class SyncQueueEffects {
  private readonly actions$ = inject(Actions);
  private readonly db = inject(OfflineDb);
  private readonly http = inject(HttpClient);
  private readonly connectivity = inject(ConnectivityService);
  private readonly runtime = inject(RuntimeConfigService);

  readonly drainWhenOnline$ = createEffect(() =>
    this.connectivity.online$.pipe(
      filter(Boolean),
      map(() => SyncQueueActions.drainRequested()),
    ),
  );

  readonly enqueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.enqueueRequested),
      concatMap(({ operation }) =>
        from(
          (async () => {
            const queued: SyncOperation = {
              ...operation,
              id: uuidv4(),
              createdAt: new Date().toISOString(),
              attempts: 0,
              nextAttemptAt: null,
              status: 'pending',
              idempotencyKey: operation.idempotencyKey ?? uuidv4(),
            };
            await this.db.syncQueue.put(queued);
            return queued;
          })(),
        ).pipe(
          exhaustMap((queued) =>
            this.connectivity.isOnline()
              ? concat(
                  of(SyncQueueActions.enqueueSucceeded({ operation: queued })),
                  of(SyncQueueActions.drainRequested()),
                )
              : of(SyncQueueActions.enqueueSucceeded({ operation: queued })),
          ),
          catchError((error: unknown) =>
            of(
              SyncQueueActions.enqueueFailed({
                error: error instanceof Error ? error.message : 'Failed to enqueue operation',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly drain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.drainRequested),
      exhaustMap(() => {
        if (!this.connectivity.isOnline()) {
          return of(SyncQueueActions.drainCompleted());
        }
        return from(this.drain()).pipe(
          map(() => SyncQueueActions.drainCompleted()),
          catchError((error: unknown) =>
            of(
              SyncQueueActions.drainFailed({
                error: error instanceof Error ? error.message : 'Failed to drain sync queue',
              }),
            ),
          ),
        );
      }),
    ),
  );

  private async drain(): Promise<void> {
    const now = Date.now();
    const pending = await this.db.syncQueue
      .where('status')
      .anyOf(['pending', 'failed'])
      .sortBy('createdAt');

    for (const operation of pending) {
      if (operation.nextAttemptAt && Date.parse(operation.nextAttemptAt) > now) {
        continue;
      }
      await this.processOne(operation);
    }
  }

  private async processOne(operation: SyncOperation): Promise<void> {
    await this.db.syncQueue.update(operation.id, { status: 'in_flight' });
    try {
      const url = operation.endpoint.startsWith('http')
        ? operation.endpoint
        : `${this.runtime.backend}${operation.endpoint}`;
      await firstValueFrom(
        this.http.request(operation.method, url, {
          body: operation.payload,
          headers: {
            ...(operation.headers ?? {}),
            'Idempotency-Key': operation.idempotencyKey,
          },
        }),
      );
      await this.db.syncQueue.delete(operation.id);
    } catch (error) {
      const attempts = operation.attempts + 1;
      const backoff = BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];
      const dead = attempts >= MAX_ATTEMPTS;
      await this.db.syncQueue.update(operation.id, {
        attempts,
        status: dead ? 'dead' : 'failed',
        lastError: error instanceof Error ? error.message : 'Sync failed',
        nextAttemptAt: dead ? null : new Date(Date.now() + backoff).toISOString(),
      });
    }
  }
}
